import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { UserPlus, Trash2, Copy, MessageSquare, Power, PowerOff, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Users() {
  const { user } = useAuth();
  const isSupport = !!user?.is_support;
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email:"", phone:"", name:"", role:"user" });
  const [tempCred, setTempCred] = useState(null);
  const [editing, setEditing] = useState(null); // {id, email, phone, name, role}

  const load = async () => setRows((await api.get("/users")).data);
  useEffect(()=>{load();},[]);

  const submit = async e => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users/invite", form);
      setTempCred({ email: data.email, password: data.temporary_password, smsSent: data.sms_sent });
      toast.success(data.sms_sent ? "Utilisateur invité — SMS envoyé" : "Utilisateur créé — copiez le mot de passe temporaire");
      setShow(false); setForm({email:"",phone:"",name:"",role:"user"}); load();
    } catch(e){ toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async id => { if(!window.confirm("Supprimer définitivement ce compte ? Cette action est irréversible."))return;
    try{ await api.delete(`/users/${id}`); load();} catch(e){ toast.error(formatApiError(e.response?.data?.detail)); } };

  const toggleActive = async (u) => {
    const willActivate = u.active === false; // currently disabled → activate
    const msg = willActivate
      ? `Réactiver le compte "${u.name}" ? L'utilisateur pourra à nouveau se connecter.`
      : `Désactiver le compte "${u.name}" ? L'utilisateur ne pourra plus se connecter, mais ses données seront conservées.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/users/${u.id}/active`, { active: willActivate });
      toast.success(willActivate ? "Compte réactivé" : "Compte désactivé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // SUPPORT ONLY : édite email/phone/name/role d'un autre user.
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const { data } = await api.patch(`/users/${editing.id}`, {
        email: editing.email,
        notification_email: editing.notification_email,
        phone: editing.phone,
        name: editing.name,
        role: editing.role,
      });
      toast.success(`Profil mis à jour (${data.changed_fields?.join(", ") || "aucun changement"})`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  // SUPPORT ONLY : réinitialise le mot de passe d'un utilisateur sans connaître l'ancien.
  const resetPassword = async (u) => {
    const pwd = window.prompt(
      `[SUPPORT] Nouveau mot de passe pour ${u.email} :\n\n` +
      "Min. 8 caractères. L'utilisateur devra le changer à sa prochaine connexion.",
    );
    if (!pwd) return;
    if (pwd.length < 8) {
      toast.error("Mot de passe trop court (min 8 caractères)");
      return;
    }
    if (!window.confirm(`Confirmer la réinitialisation du mot de passe de ${u.email} ?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, { new_password: pwd });
      setTempCred({ email: data.user_email, password: pwd, smsSent: false });
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Utilisateurs" subtitle="Permissions · Rôles · Invitations"
        actions={
          <div className="flex gap-2">
            <button data-testid="invite-user-btn" className="btn-primary flex items-center gap-2" onClick={()=>{setShow(!show); setTempCred(null);}}><UserPlus size={14}/>Inviter</button>
          </div>
        }
      />

      {tempCred && (
        <Surface title="Identifiants temporaires" testId="temp-cred-panel" action={<button onClick={()=>setTempCred(null)} className="btn-ghost text-xs">Fermer</button>}>
          <div className="p-5 space-y-3">
            <p className="text-sm text-[var(--text-muted)]">
              {tempCred.smsSent ? <>📱 SMS envoyé à l'utilisateur. <span className="text-[var(--neon-cyan)]">Voici aussi les identifiants pour référence :</span></> : <>⚠️ Pas de téléphone fourni — copiez et transmettez les identifiants manuellement.</>}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="surface p-3">
                <div className="label-mono mb-1">Email</div>
                <div className="font-data flex items-center justify-between">{tempCred.email}<button onClick={()=>copy(tempCred.email)} className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)]"><Copy size={12}/></button></div>
              </div>
              <div className="surface p-3">
                <div className="label-mono mb-1">Mot de passe temporaire</div>
                <div className="font-data flex items-center justify-between neon-text-cyan">{tempCred.password}<button onClick={()=>copy(tempCred.password)} className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)]"><Copy size={12}/></button></div>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">L'utilisateur devra changer ce mot de passe à sa première connexion.</p>
          </div>
        </Surface>
      )}

      {show && (
        <Surface title="Inviter un utilisateur">
          <form onSubmit={submit} className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><label className="label-mono block mb-1.5">Nom</label><input data-testid="user-name" required className="input-base" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Email</label><input data-testid="user-email" type="email" required className="input-base" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Téléphone (SMS)</label><input data-testid="user-phone" placeholder="+221 77..." className="input-base" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Rôle</label>
              <select data-testid="user-role" className="input-base" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                <option value="admin">Admin</option><option value="user">Utilisateur</option><option value="viewer">Lecture seule</option>
              </select>
            </div>
            <div className="col-span-full text-xs text-[var(--text-muted)]"><MessageSquare size={12} className="inline mr-1 text-[var(--neon-cyan)]"/>Si un téléphone est saisi, un SMS sera automatiquement envoyé avec les identifiants.</div>
            <div className="col-span-full flex gap-2"><button data-testid="submit-user" type="submit" className="btn-primary">Envoyer l'invitation</button><button type="button" className="btn-ghost" onClick={()=>setShow(false)}>Annuler</button></div>
          </form>
        </Surface>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={saveEdit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" data-testid="edit-user-modal">
            <div>
              <div className="font-heading font-extrabold text-xl">Modifier l'utilisateur</div>
              <div className="text-xs text-[var(--text-muted)] mt-1">Outil support — mise à jour profil cliente</div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-mono block mb-1.5">Nom</label>
                <input data-testid="edit-name" className="input-base" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Email (sert au login + reset password)</label>
                <input data-testid="edit-email" type="email" className="input-base" value={editing.email} onChange={(e) => setEditing({...editing, email: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Email de notification (recovery, alertes)</label>
                <input data-testid="edit-notification-email" type="email" placeholder="ex: prenom@yahoo.com" className="input-base" value={editing.notification_email} onChange={(e) => setEditing({...editing, notification_email: e.target.value})}/>
                <p className="text-xs text-[var(--text-muted)] mt-1">Si différent de l'email de login, les OTP de reset password sont envoyés aux DEUX adresses.</p>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Téléphone (SMS reset password) — format +221…</label>
                <input data-testid="edit-phone" placeholder="+221 77 369 95 25" className="input-base" value={editing.phone} onChange={(e) => setEditing({...editing, phone: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Rôle</label>
                <select data-testid="edit-role" className="input-base" value={editing.role} onChange={(e) => setEditing({...editing, role: e.target.value})}>
                  <option value="admin">Admin</option>
                  <option value="user">Utilisateur</option>
                  <option value="viewer">Lecture seule</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button data-testid="save-edit-user" type="submit" className="btn-primary">Enregistrer</button>
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      <Surface>
        {rows.length === 0 ? <Empty/> : (
          <table className="data-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Rôle</th><th>Créé</th><th></th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} className={r.active === false ? "opacity-60" : ""}>
                <td className="font-medium">
                  {r.name}
                  {r.must_change_password && <span className="badge-warn ml-2">À CHANGER</span>}
                  {r.active === false && <span className="badge-warn ml-2 bg-[var(--alert-soft,#FDECEC)] text-[var(--alert,#EF4444)]">DÉSACTIVÉ</span>}
                </td>
                <td className="font-data text-[var(--text-muted)]">{r.email}</td>
                <td className="font-data text-[var(--text-muted)]">{r.phone || "—"}</td>
                <td><span className={r.role === "admin" ? "badge-info" : r.role === "user" ? "badge-success" : "badge-warn"}>{r.role}</span></td>
                <td className="font-data text-[var(--text-muted)] text-xs">{r.created_at?.slice(0,10)}</td>
                <td>
                  {r.id !== user?.id && (
                    <div className="flex items-center gap-2 justify-end">
                      {isSupport && (
                        <button
                          data-testid={`edit-user-${r.email}`}
                          onClick={() => setEditing({ id: r.id, email: r.email || "", notification_email: r.notification_email || "", phone: r.phone || "", name: r.name || "", role: r.role || "user" })}
                          className="text-[var(--text-muted)] hover:text-[var(--primary-dark)]"
                          title="[SUPPORT] Modifier email / téléphone / nom"
                        >
                          <Pencil size={14}/>
                        </button>
                      )}
                      {isSupport && (
                        <button
                          data-testid={`reset-password-${r.email}`}
                          onClick={() => resetPassword(r)}
                          className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)]"
                          title="[SUPPORT] Réinitialiser le mot de passe"
                        >
                          <KeyRound size={14}/>
                        </button>
                      )}
                      <button
                        data-testid={`toggle-active-${r.email}`}
                        onClick={() => toggleActive(r)}
                        className={r.active === false
                          ? "text-emerald-600 hover:text-emerald-700"
                          : "text-[var(--text-muted)] hover:text-amber-600"}
                        title={r.active === false ? "Réactiver le compte" : "Désactiver le compte"}
                      >
                        {r.active === false ? <Power size={14}/> : <PowerOff size={14}/>}
                      </button>
                      <button
                        data-testid={`delete-user-${r.email}`}
                        onClick={() => remove(r.id)}
                        className="text-[var(--text-muted)] hover:text-[var(--neon-pink)]"
                        title="Supprimer le compte"
                      >
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
